import { Button, Autocomplete, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import BudgetControlModal from "../BudgetControlModal/BudgetControlModal";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import { saveAs } from "file-saver";
import { useSearchParams } from "react-router-dom";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { Stack } from "@mui/system";
import DupliCateBrandData from "../../DuplicateBudget/DupliCateBrandData";
import {
  setDuplicateFrom,
  setEditedBudgets,
  setSelectedBudgetBrands,
} from "../../../../../features/engagementIncentivesSlice";
import { useDispatch } from "react-redux";
interface BudgetControlFilterProps {
  period: any;
  setPeriod: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  setShow: any;
  setDisplay: any;
  exequteQueryContentQueryCreate: any;
  userToken: string;
  setRefresh: any;
  sortedRows: any;
  tableHeadings: any;
}

const BudgetControlFilter = ({
  period,
  setPeriod,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setShow,
  setDisplay,
  exequteQueryContentQueryCreate,
  userToken,
  setRefresh,
  sortedRows,
  tableHeadings,
}: BudgetControlFilterProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [, setExportOpenModal] = useState(false);
  const [monthRange, setMonthRange] = useState<{ label: string }[]>([]);
  const dispatch = useDispatch();
  const exequteQueryContentQueryCreateData =
    exequteQueryContentQueryCreate?.query_output || [];
  const userData = useSelector(selectUserData);

  const hasCreateMonthlyBudgetPermission = CheckPermissions(
    userData?.data?.products?.paid_traffic,
    "advertising_accounts",
    "create_monthly_budgets"
  );
  const hasViewMonthlyBudgetPermission = CheckPermissions(
    userData?.data?.products?.paid_traffic,
    "advertising_accounts",
    "view_monthly_budgets"
  );
  const hasEditMonthlyBudgetPermission = CheckPermissions(
    userData?.data?.products?.paid_traffic,
    "advertising_accounts",
    "edit_monthly_budgets"
  );
  const hasviewIncentivezedTrafficPerformanceMonthlyBudgetPermission =
    CheckPermissions(
      userData?.data?.products?.paid_traffic,
      "advertising_accounts",
      "view_incentivezed_traffic_performance"
    );
  const headCells = Object.entries(tableHeadings);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setExportOpenModal(false);
  };

  // State to manage the visibility of the duplicate budget dialog
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
    dispatch(setEditedBudgets([]));
  };

  const handleClose = () => {
    dispatch(setDuplicateFrom({ startDate: null, endDate: null }));
    dispatch(setSelectedBudgetBrands([]));
    setOpen(false);
  };

  // Generate last 23 months including the current month
  const generateMonthRange = () => {
    const months = [];
    const currentMonth = dayjs(); // Get the current date using dayjs

    // Generate last 23 months + 3 future months (total 27 months)
    for (let i = 23; i >= -3; i--) {
      const month = currentMonth.subtract(i, "month").format("MM/YYYY");
      months.push({ label: month });
    }

    return months.reverse(); // Now the list starts with the current month and includes future months
  };

  useEffect(() => {
    const months = generateMonthRange();
    setMonthRange(months.reverse());

    // Find and set the default period to the actual current month
    if (!period) {
      const currentMonth = dayjs().format("MM/YYYY"); // Get the current month using dayjs
      const currentMonthIndex = months.findIndex(
        (month) => month.label === currentMonth
      );

      if (currentMonthIndex !== -1) {
        setPeriod(currentMonth); // Set current month as the period
        calculateStartAndEndDate(currentMonth); // Calculate start and end date
      }
    }
    // eslint-disable-next-line
  }, []); // Empty dependency array to run only once on mount

  const calculateStartAndEndDate = (selectedPeriod: string) => {
    const [month, year] = selectedPeriod.split("/").map(Number);
    const start = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD"); // First day of the month
    const end = dayjs(start).endOf("month").format("YYYY-MM-DD"); // Last day of the month

    setStartDate(start);
    setEndDate(end);
  };

  const handleShow = () => {
    if (period && startDate && endDate) {
      setShow(true);
      setDisplay(true);

      // Update search params with period, startDate, and endDate
      const updatedParams = new URLSearchParams(searchParams.toString());
      updatedParams.set("periodTab1", period);
      updatedParams.set("startDateTab1", startDate);
      updatedParams.set("endDateTab1", endDate);
      setSearchParams(updatedParams);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const periodParam = params.get("periodTab1");
    const startDateParam = params.get("startDateTab1");
    const endDateParam = params.get("endDateTab1");

    if (periodParam) {
      setPeriod(periodParam);
      calculateStartAndEndDate(periodParam);
    }

    if (startDateParam) setStartDate(startDateParam);
    if (endDateParam) setEndDate(endDateParam);
    // eslint-disable-next-line
  }, []); // Run only on mount

  const handleExportCSV = () => {
    const csvRows = [];

    // Filter out the 'info' header
    const filteredHeadCells = headCells.filter(([key]) => key !== "info");

    // Extract the table headers
    const headers = filteredHeadCells.map(([_, value]) => value);
    csvRows.push(headers.join(","));

    // Extract the table data
    sortedRows.forEach((row: any) => {
      const rowData = filteredHeadCells.map(([key]) => {
        if (key === "total_cost") {
          return `"${(
            row.total_cost * row.qftd_actual || 0
          ).toLocaleString()}$"`;
        } else if (key === "utilization") {
          return `${Math.round(row.utilization * 100)}%`;
        } else if (key === "cost_per_qftd") {
          return row.cost_per_qftd !== null && row.cost_per_qftd
            ? `${row.cost_per_qftd}$`
            : "-";
        } else if (key === "brand_name" || key === "modified_by") {
          return row[key] || "";
        }
        return row[key] || "0";
      });
      csvRows.push(rowData.join(","));
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "table_data_budget_control.csv");
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: "0px" }}
      >
        <Grid size={3} mt={2}>
          <Stack direction="row" spacing={2}>
            <Autocomplete
              disablePortal
              options={monthRange.slice().reverse()}
              value={period ? { label: period } : null}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Period" color="secondary" />
              )}
              onChange={(_, value) => {
                setPeriod(value?.label || "");
                if (value) {
                  calculateStartAndEndDate(value.label); // Calculate dates when period changes
                }
              }}
              onInputChange={(_, __, reason) => {
                if (reason === "clear") {
                  setPeriod(null);
                  setStartDate(null);
                  setEndDate(null);
                  setDisplay(false);
                }
              }}
              size="small"
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={handleShow}
              size="medium"
              disabled={!period}
              data-tracker="189"
            >
              Show
            </Button>
          </Stack>
        </Grid>
        <Grid size={9} mt={2} textAlign="right">
          <Button
            color="secondary"
            sx={{ marginRight: 2 }}
            onClick={handleExportCSV}
            data-tracker="190"
          >
            Export
          </Button>
          {hasCreateMonthlyBudgetPermission &&
          hasViewMonthlyBudgetPermission &&
          hasEditMonthlyBudgetPermission &&
          hasviewIncentivezedTrafficPerformanceMonthlyBudgetPermission ? (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ marginRight: 2 }}
              onClick={handleClickOpen}
              data-tracker="367"
            >
              Duplicate existing budget
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ marginRight: 2 }}
              disabled
              data-tracker="367"
            >
              Duplicate existing budget
            </Button>
          )}

          {hasCreateMonthlyBudgetPermission ? (
            <Button
              variant="contained"
              color="secondary"
              endIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={!startDate}
              data-tracker="195"
            >
              Add new budget
            </Button>
          ) : (
            <Button variant="contained" disabled endIcon={<AddIcon />}>
              Add new budget
            </Button>
          )}
        </Grid>
        <BudgetControlModal
          open={openModal}
          handleClose={handleCloseModal}
          exequteQueryContentQueryCreateData={
            exequteQueryContentQueryCreateData
          }
          userToken={userToken}
          startDate={startDate}
          setRefresh={setRefresh}
        />
        <DupliCateBrandData open={open} handleClose={handleClose} />
      </Grid>
    </>
  );
};

export default BudgetControlFilter;
